// ============================================================================
// FSA TEMPLATE UPGRADE / SWITCH SERVICE (ADR-0004 Phase 2 + any-template switch)
// ============================================================================
// Backend controller: FsaTemplateUpgradesController → /api/v1/FsaTemplateUpgrades

import { get, post } from '../../client/axiosClient';
import { buildEndpoint } from '../../endpoints/base.endpoints';
import type { ApiResponse, ID } from '../../types';
import type {
  TemplateSwitchTarget,
  TemplateUpgradePreview,
  TemplateUpgradeResult,
} from '../../types/fsa/templateUpgrade.types';

class FsaTemplateUpgradeService {
  /** Templates the engagement can move to — latest version of each installed template + its current pin. */
  async targets(engagementId: ID): Promise<TemplateSwitchTarget[]> {
    const response = await get<ApiResponse<TemplateSwitchTarget[]>>(
      buildEndpoint(`/FsaTemplateUpgrades/by-engagement/${engagementId}/targets`),
    );
    return (response.data.data as TemplateSwitchTarget[]) ?? [];
  }

  /** Code-mapped preview of moving the engagement to targetTemplateId (latest version of its own template when omitted). */
  async preview(engagementId: ID, targetTemplateId?: ID | null): Promise<TemplateUpgradePreview> {
    const qs = targetTemplateId ? `?targetTemplateId=${targetTemplateId}` : '';
    const response = await get<ApiResponse<TemplateUpgradePreview>>(
      buildEndpoint(`/FsaTemplateUpgrades/by-engagement/${engagementId}/preview${qs}`),
    );
    return response.data.data as TemplateUpgradePreview;
  }

  /** Applies the change transactionally (re-keys every engagement reference by code). */
  async upgrade(engagementId: ID, targetTemplateId: ID): Promise<TemplateUpgradeResult> {
    const response = await post<ApiResponse<TemplateUpgradeResult>>(
      buildEndpoint(`/FsaTemplateUpgrades/by-engagement/${engagementId}`),
      { targetTemplateId },
    );
    return response.data.data as TemplateUpgradeResult;
  }
}

export const fsaTemplateUpgradeService = new FsaTemplateUpgradeService();
export default fsaTemplateUpgradeService;
